import type {
  PolicyHistoryFilter,
  PolicyCondition,
  PolicyMissingCostBehavior,
  PolicyStockRequirement,
  PolicyUnknownCompetitorBehavior,
  PriceMatchPolicy as ApiPriceMatchPolicy,
  PriceMatchPolicyOutcome,
} from "@price-rescue/api-contracts";

export type { PolicyCondition, PolicyStockRequirement };
export type PriceMatchPolicy = ApiPriceMatchPolicy;
export type PolicyOutcome = PriceMatchPolicyOutcome;
export type PolicyOverrideScope = "PRODUCT" | "VARIANT";

export interface PolicySourceEntry {
  scope: string;
  sourceId: string;
  enabled: boolean;
}

export interface PriceMatchPolicyDto {
  scope: "STORE" | "PRODUCT" | "VARIANT";
  configured: boolean;
  enabled: boolean;
  policy: Partial<PriceMatchPolicy>;
  effectivePolicy: PriceMatchPolicy;
  sourceChain: PolicySourceEntry[];
}

export type PolicyDiscountStacking = "BLOCK" | "ALLOW";

export interface MerchantPolicySettings {
  policyEnabled: boolean;
  maxDiscountBps: number;
  maximumQuantity: number;
  minimumMarginBps?: number;
  minimumOfferPriceMinor?: number;
  missingCostBehavior: PolicyMissingCostBehavior;
  unknownCompetitorBehavior: PolicyUnknownCompetitorBehavior;
  allowCounteroffer: boolean;
  allowManualReview: boolean;
  offerExpiryMinutes: number;
  discountStacking: PolicyDiscountStacking;
}

export interface MerchantPolicyStats {
  policyStatus: "ACTIVE" | "INACTIVE";
  maxDiscountLabel: string;
  minimumMarginLabel: string;
  offerExpiryLabel: string;
}

export interface MerchantPolicyCoverage {
  productSampleSize: number;
  variantSampleSize: number;
  missingIdentifiers: number;
  missingCost: number;
  unavailableInventory: number;
  notes: string[];
}

export interface MerchantPolicyProtection {
  key: string;
  label: string;
  value: string;
  description: string;
}

export interface MerchantPolicyProductOverride {
  productId: string;
  title: string;
  enabled: boolean;
  updatedAt: string;
  summary: string;
}

export interface MerchantPolicyHistory {
  changedAt: string;
  actorId: string;
  actorDisplayName?: string;
  summary: string;
  note?: string;
  beforeSummary?: Record<string, unknown>;
  afterSummary?: Record<string, unknown>;
}

export interface MerchantPolicyHistoryList {
  items: MerchantPolicyHistory[];
  pagination: {
    currentCursor: string | null;
    nextCursor: string | null;
  };
  filters: {
    filter: PolicyHistoryFilter;
    limit: number;
    dateFrom?: string;
    dateTo?: string;
  };
}

export interface MerchantPolicyHistoryFilters {
  filter: PolicyHistoryFilter;
  limit: number;
  cursor?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface MerchantPolicyAllowedActions {
  canEdit: boolean;
  canActivate: boolean;
  disabledReason?: string;
}

export interface MerchantPolicySectionState {
  section: "COVERAGE" | "PRODUCT_OVERRIDES" | "HISTORY";
  state: "READY" | "UNAVAILABLE" | "EMPTY";
  message: string;
}

export interface MerchantPolicyPage {
  settings: MerchantPolicySettings;
  stats: MerchantPolicyStats;
  coverage: MerchantPolicyCoverage;
  protections: MerchantPolicyProtection[];
  productOverrides: MerchantPolicyProductOverride[];
  history: MerchantPolicyHistory[];
  allowedActions: MerchantPolicyAllowedActions;
  sectionStates: MerchantPolicySectionState[];
}

export interface MerchantPolicyValidation {
  valid: boolean;
  errors: PolicyValidationIssue[];
  warnings: PolicyValidationIssue[];
  protectionChecks: MerchantPolicyProtection[];
}

export interface MerchantPolicyActivationInput {
  settings: MerchantPolicySettings;
  reason: string;
  acknowledgedMarginRisk: boolean;
  acknowledgedLegalReview: boolean;
}

export interface PolicyFormDraft extends PriceMatchPolicy {
  policyName: string;
  policyIdentity: string;
  notes: string;
}

export interface PolicyActivationDraft {
  reason: string;
  acknowledgedMarginRisk: boolean;
  acknowledgedLegalReview: boolean;
}

export interface PolicyPreviewSample {
  currentPriceMinor: number;
  competitorPriceMinor: number;
  currency: string;
  competitorCurrency: string;
  competitorDomainApproved: boolean;
  sellerApproved: boolean;
  competitorCondition: PolicyCondition | "USED" | "REFURBISHED";
  competitorInStock: boolean;
  observedAt: string;
  now: string;
  isPublicPrice: boolean;
  requestCountForCustomerToday?: number;
  requestCountForProductToday?: number;
  unitCostMinor?: number;
}

export interface PolicyPreviewResult {
  outcome: PolicyOutcome;
  publicReasonCodes: string[];
  missingData: string[];
  offerPriceMinor?: number;
  counterofferPriceMinor?: number;
  policySchemaVersion: string;
}

export interface PolicyOverrideDraft {
  scope: PolicyOverrideScope;
  scopeId: string;
  variantId?: string;
  configured: boolean;
  enabled: boolean;
  policyOverride: Partial<PriceMatchPolicy>;
}

export interface PolicyValidationIssue {
  field: keyof PolicyFormDraft | string;
  message: string;
}

export interface PolicySaveResult {
  dto: PriceMatchPolicyDto;
  savedAs: "DRAFT" | "ACTIVE";
}

export function defaultPolicyDraft(): PolicyFormDraft {
  return {
    policyName: "Global price-match policy",
    policyIdentity: "STORE_DEFAULT",
    notes: "",
    maxDiscountBps: 2_500,
    competitorMustBeInStock: true,
    sameVariantRequired: true,
    samePackRequired: true,
    allowedCondition: "NEW",
    offerTtlMinutes: 30,
    allowCounteroffer: true,
    allowManualReview: true,
    maximumQuantity: 1,
    stockRequirement: "IN_STOCK",
    maximumPriceAgeMinutes: 60,
    requirePublicPrice: true,
    automaticApprovalMaxDiscountBps: 1_500,
    manualReviewAboveDiscountBps: 2_500,
    counterofferMaxDiscountBps: 2_000,
    offerExpiryMinutes: 30,
    redemptionLimit: 1,
    allowStacking: false,
  };
}

export function defaultMerchantPolicySettings(): MerchantPolicySettings {
  return {
    policyEnabled: true,
    maxDiscountBps: 2_500,
    maximumQuantity: 1,
    minimumMarginBps: 1_500,
    missingCostBehavior: "MANUAL_REVIEW",
    unknownCompetitorBehavior: "MANUAL_REVIEW",
    allowCounteroffer: true,
    allowManualReview: true,
    offerExpiryMinutes: 30,
    discountStacking: "BLOCK",
  };
}

export function policyFromMerchantSettings(settings: MerchantPolicySettings): PriceMatchPolicy {
  return withUndefinedListsRemoved({
    maxDiscountBps: settings.maxDiscountBps,
    maximumQuantity: settings.maximumQuantity,
    minimumMarginBps: settings.minimumMarginBps,
    minimumOfferPriceMinor: settings.minimumOfferPriceMinor,
    missingCostBehavior: settings.missingCostBehavior,
    unknownCompetitorBehavior: settings.unknownCompetitorBehavior,
    competitorMustBeInStock: true,
    sameVariantRequired: true,
    samePackRequired: true,
    allowedCondition: "NEW",
    offerTtlMinutes: settings.offerExpiryMinutes,
    allowCounteroffer: settings.allowCounteroffer,
    allowManualReview: settings.allowManualReview,
    stockRequirement: "IN_STOCK",
    maximumPriceAgeMinutes: 60,
    requirePublicPrice: true,
    automaticApprovalMaxDiscountBps: Math.min(settings.maxDiscountBps, 1_500),
    manualReviewAboveDiscountBps: settings.maxDiscountBps,
    offerExpiryMinutes: settings.offerExpiryMinutes,
    redemptionLimit: 1,
    allowStacking: settings.discountStacking === "ALLOW",
  } as PriceMatchPolicy);
}

export function defaultPolicyPreviewSample(currency = "USD"): PolicyPreviewSample {
  const now = new Date().toISOString();
  return {
    currentPriceMinor: 12_999,
    competitorPriceMinor: 11_999,
    currency,
    competitorCurrency: currency,
    competitorDomainApproved: true,
    sellerApproved: true,
    competitorCondition: "NEW",
    competitorInStock: true,
    observedAt: now,
    now,
    isPublicPrice: true,
    requestCountForCustomerToday: 0,
    requestCountForProductToday: 0,
  };
}

export function defaultActivationDraft(): PolicyActivationDraft {
  return {
    reason: "",
    acknowledgedMarginRisk: false,
    acknowledgedLegalReview: false,
  };
}

export function defaultOverrideDraft(): PolicyOverrideDraft {
  return {
    scope: "PRODUCT",
    scopeId: "",
    configured: false,
    enabled: true,
    policyOverride: {
      maxDiscountBps: 2_000,
      allowCounteroffer: true,
      minimumMarginBps: 1_500,
    },
  };
}

export function draftFromPolicy(policy: PriceMatchPolicy): PolicyFormDraft {
  return {
    ...defaultPolicyDraft(),
    ...policy,
    policyName: "Global price-match policy",
    policyIdentity: policy.schemaVersion ?? "STORE_DEFAULT",
    notes: "",
  };
}

export function policyFromDraft(draft: PolicyFormDraft): PriceMatchPolicy {
  const {
    policyName: _policyName,
    policyIdentity: _policyIdentity,
    notes: _notes,
    ...policy
  } = draft;
  return withUndefinedListsRemoved(policy);
}

export function validatePolicyDraft(draft: PolicyFormDraft): PolicyValidationIssue[] {
  return [
    validateBps("maxDiscountBps", draft.maxDiscountBps),
    validateBps("minimumMarginBps", draft.minimumMarginBps),
    validateBps("automaticApprovalMaxDiscountBps", draft.automaticApprovalMaxDiscountBps),
    validateBps("manualReviewAboveDiscountBps", draft.manualReviewAboveDiscountBps),
    validateBps("counterofferMaxDiscountBps", draft.counterofferMaxDiscountBps),
    validatePositive("offerTtlMinutes", draft.offerTtlMinutes),
    validatePositive("maximumQuantity", draft.maximumQuantity),
    validatePositive("maximumPriceAgeMinutes", draft.maximumPriceAgeMinutes),
    validatePositive("offerExpiryMinutes", draft.offerExpiryMinutes),
    validatePositive("redemptionLimit", draft.redemptionLimit),
    validateThresholdOrder(draft),
  ].filter((issue): issue is PolicyValidationIssue => issue !== undefined);
}

export function hasMarginRules(draft: PolicyFormDraft): boolean {
  return draft.minimumMarginBps !== undefined || draft.minimumOfferPriceMinor !== undefined;
}

function validateBps(
  field: keyof PolicyFormDraft,
  value: number | undefined,
): PolicyValidationIssue | undefined {
  if (value === undefined) return undefined;
  if (value >= 0 && value <= 10_000) return undefined;
  return { field, message: "Must be between 0 and 10000 basis points." };
}

function validatePositive(
  field: keyof PolicyFormDraft,
  value: number | undefined,
): PolicyValidationIssue | undefined {
  if (value === undefined) return undefined;
  if (Number.isInteger(value) && value > 0) return undefined;
  return { field, message: "Must be a positive whole number." };
}

function validateThresholdOrder(draft: PolicyFormDraft): PolicyValidationIssue | undefined {
  const auto = draft.automaticApprovalMaxDiscountBps;
  const review = draft.manualReviewAboveDiscountBps;
  if (auto === undefined || review === undefined || auto <= review) return undefined;
  return {
    field: "manualReviewAboveDiscountBps",
    message: "Manual-review threshold must be at or above automatic approval threshold.",
  };
}

function withUndefinedListsRemoved<T extends object>(policy: T): T {
  return Object.fromEntries(
    Object.entries(policy).filter(([, value]) => value !== undefined && value !== ""),
  ) as unknown as T;
}
